/**
 * @fileOverview The flag that tells an app instance it is the one inside the Erklärung's frame.
 *
 * The per-view walkthrough shows the **real product** in an iframe rather than a recording of it, so
 * a second instance of this app boots inside the first one. That instance has to behave differently
 * in a few places, and all of them need the answer before the router exists — so it is read straight
 * from `window.location` once, at module load, and never from `useRoute()`.
 *
 * Deliberately its own flag and not `?popup=true`: that one means the detached planning calendar,
 * which is a window the user opened and can interact with. This one means "you are a picture of the
 * product being explained", and the two want different things.
 *
 * See docs/view-help.md.
 */

/** Query parameter, and the value it has to carry. */
const EMBED_PARAM = "embed"
const EMBED_VALUE = "view-help"

/**
 * The frame's own `name` — which, unlike its URL, survives every navigation inside the frame. A full
 * document load the app makes on its own (the chunk-error reload, the release swap) goes to a path
 * without the query flag; the name is still there, so that instance boots sealed all the same.
 */
export const VIEW_HELP_FRAME_NAME = "lewara-view-help"

/**
 * Whether `win` is an app instance inside the Erklärung's frame: it is framed at all, and carries the
 * flag in its URL or the frame's name.
 *
 * Framed first, because neither mark proves it on its own: `window.open(url, "lewara-view-help")` or a
 * link with that `target` gives an ordinary tab the same name, and anybody can type the query flag.
 * Such a tab booted sealed — sample data instead of the workspace, and every save refused without a
 * snackbar — so the user believed their changes had been saved.
 */
export const isViewHelpEmbedWindow = (win: Window): boolean =>
  win.parent !== win &&
  (win.name === VIEW_HELP_FRAME_NAME || new URLSearchParams(win.location.search).get(EMBED_PARAM) === EMBED_VALUE)

/**
 * True in the instance rendered inside the frame.
 *
 * Constant for the lifetime of the app: the frame navigates by reloading, so an instance is either
 * embedded from its first line or never.
 */
export const IS_VIEW_HELP_EMBED = typeof window !== "undefined" && isViewHelpEmbedWindow(window)

/**
 * The mark `isolation.ts` leaves on a window it has sealed — read by the player before it drives one.
 * On the window rather than in a module, because the host asks it of another app instance.
 */
export const VIEW_HELP_SEALED_MARK = "__lewaraViewHelpSealed"

/** Whether the app instance in `win` really sealed itself off, rather than only being named so. */
export const isSealedViewHelpWindow = (win: Window): boolean =>
  (win as unknown as Record<string, unknown>)[VIEW_HELP_SEALED_MARK] === true

/**
 * Where a full document load inside the app should go. In the frame it keeps the embed flag: a load to
 * the bare path would boot the **unsealed** app there — no sandbox, no write guard, the reader's own
 * data under the walkthrough's pointer. Everywhere else it is the path itself.
 */
export const documentLoadUrl = (path: string): string => (IS_VIEW_HELP_EMBED ? viewHelpEmbedUrl(path) : path)

/**
 * The URL to point the frame at for a route.
 *
 * Built from the router's own resolution rather than assembled by hand, so a step naming a route can
 * never disagree with where that route actually lives.
 */
export function viewHelpEmbedUrl(path: string): string {
  const url = new URL(path, window.location.origin)
  url.searchParams.set(EMBED_PARAM, EMBED_VALUE)
  return url.toString()
}
